import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { useState, useEffect, lazy, Suspense } from 'react';
import RequireAuth from './components/RequireAuth';
import { checkAuthStatus } from './api/user';
import AppShell from './components/AppShell/AppShell';
import { isMarketingHost } from './config/site';

// Eagerly loaded — needed on first paint for unauthenticated visitors
import Login from './pages/Login';
import AboutPage from './pages/AboutPage';
import LogoutPage from './pages/LogoutPage';

// Lazily loaded — split into separate chunks, only fetched when navigated to
const DashboardPage        = lazy(() => import('./pages/DashboardPageManifest'));
const ProfilePage          = lazy(() => import('./pages/ProfilePageManifest'));
const CategoriesPage       = lazy(() => import('./pages/CategoriesPage'));
const CategorySetsPage     = lazy(() => import('./pages/CategorySetsPage'));
const CategorySetEditorPage = lazy(() => import('./pages/CategorySetEditorPage'));
const RulesPage            = lazy(() => import('./pages/RulesPage'));
const DigestPage           = lazy(() => import('./pages/DigestPage'));
const AnalysisPage         = lazy(() => import('./pages/AnalysisPage'));
const BillingPage          = lazy(() => import('./pages/BillingPage'));
const TermsPage            = lazy(() => import('./pages/TermsPage'));
const PrivacyPage          = lazy(() => import('./pages/PrivacyPage'));
const AdvertPreviewPage    = lazy(() => import('./pages/advert/AdvertPreviewPage'));
const UsersPage            = lazy(() => import('./pages/UsersPage'));
const UserDetailPage       = lazy(() => import('./pages/UserDetailPage'));
const OrganisationPage     = lazy(() => import('./pages/OrganisationPage'));

function PageSpinner() {
  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center">
      <div className="spinner-border text-primary" role="status" aria-label="Loading" />
    </div>
  );
}

/** Scrolls to the top of the page on every route change. */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/** Redirects legacy /category-sets/:setId bookmarks to the new /categories/:setId path. */
function LegacySetRedirect() {
  const { setId } = useParams<{ setId: string }>();
  return <Navigate to={`/categories/${setId}`} replace />;
}

/**
 * Root route: shows AboutPage to unauthenticated visitors; redirects
 * authenticated users straight to /dashboard.
 */
function RootRoute() {
  const [status, setStatus] = useState<'loading' | 'authed' | 'anon'>('loading');
  const location = useLocation();

  useEffect(() => {
    checkAuthStatus()
      .then((authed) => setStatus(authed ? 'authed' : 'anon'))
      .catch(() => setStatus('anon'));
  }, []);

  if (status === 'loading') {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status" aria-label="Loading" />
      </div>
    );
  }

  if (status === 'authed') {
    const storedPlan = sessionStorage.getItem('catty_post_auth_plan');
    if (storedPlan && ['trial', 'solo', 'team'].includes(storedPlan)) {
      sessionStorage.removeItem('catty_post_auth_plan');
      return <Navigate to={`/billing?plan=${storedPlan}`} replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  const params = new URLSearchParams(location.search);
  const hasAuthCallbackState =
    params.has('auth_error') ||
    params.has('consent_error') ||
    params.has('consent_granted');

  if (hasAuthCallbackState) {
    return <Navigate to={`/login${location.search}`} replace />;
  }

  return <AboutPage />;
}

/**
 * Marketing host (catty.joinedupdata.io): brand-domain marketing site only.
 * No auth, no app routes — CTAs link across to the app on catty.api-jud.io.
 */
function MarketingRoutes() {
  return (
    <Routes>
      <Route path="/" element={<AboutPage />} />
      <Route path="/about" element={<Navigate to="/" replace />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Suspense fallback={<PageSpinner />}>
        {isMarketingHost() ? <MarketingRoutes /> : (
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/logout" element={<LogoutPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/internal/advert-preview" element={<AdvertPreviewPage />} />

          {/* Authenticated routes — wrapped in RequireAuth + AppShell */}
          <Route
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            <Route path="/dashboard"              element={<DashboardPage />} />
            <Route path="/analysis"               element={<AnalysisPage />} />

            {/* Categories — single-set-first UX */}
            <Route path="/categories"             element={<CategoriesPage />} />
            <Route path="/categories/manage"      element={<CategorySetsPage />} />
            <Route path="/categories/:setId"      element={<CategorySetEditorPage />} />

            {/* Legacy URL aliases (backwards compat) */}
            <Route path="/category-sets"          element={<Navigate to="/categories" replace />} />
            <Route path="/category-sets/:setId"   element={<LegacySetRedirect />} />

            <Route path="/rules"                  element={<RulesPage />} />
            <Route path="/digest"                 element={<DigestPage />} />
            <Route path="/billing"                element={<BillingPage />} />
            <Route path="/billing/success"        element={<BillingPage />} />
            <Route path="/billing/cancel"         element={<BillingPage />} />
            <Route path="/users"                  element={<UsersPage />} />
            <Route path="/users/:userId"          element={<UserDetailPage />} />
            <Route path="/organisation"           element={<OrganisationPage />} />
            <Route path="/profile"                element={<ProfilePage />} />
          </Route>

          {/* Root: AboutPage for anon visitors, /dashboard for authenticated users */}
          <Route path="/" element={<RootRoute />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        )}
      </Suspense>
    </BrowserRouter>
  );
}

